"use client";

import React, { useState, useEffect, useRef } from "react";
import { PageHomeSchema } from "@/sanity/pages/PageHomeTemplate";
import { PageFuturesSchema } from "@/sanity/pages/PageFuturesTemplate";

import { cn } from "@/lib/utils";
import Image from "next/image";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/shadcn/ui/carousel";
import ChipSerif from "@/components/global/ui/ChipSerif";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

import clockTower from "@/public/home/clockTower.png";
import basti from "@/public/home/basti.png";

function sectorPath(startAngle: number, endAngle: number) {
  const R = 1.02;
  const EPS = 0.002;

  const a0 = startAngle - EPS;
  const a1 = endAngle + EPS;

  const x1 = Math.cos(a0) * R;
  const y1 = Math.sin(a0) * R;
  const x2 = Math.cos(a1) * R;
  const y2 = Math.sin(a1) * R;

  const largeArcFlag = a1 - a0 > Math.PI ? 1 : 0;

  return `
    M 0 0
    L ${x1} ${y1}
    A ${R} ${R} 0 ${largeArcFlag} 1 ${x2} ${y2}
    Z
  `;
}

const SLICE_COUNT = 9;

function PieOverlay({
  removedCount,
  className,
}: {
  removedCount: number;
  className?: string;
}) {
  const sliceAngle = (2 * Math.PI) / SLICE_COUNT;
  const base = -Math.PI / 2 - sliceAngle / 2;

  // The two “outermost” hidden slices that border the revealed region
  const hasBorders = removedCount > 0 && removedCount < SLICE_COUNT;
  const frontIndex = hasBorders ? removedCount : null;
  const backIndex = hasBorders ? SLICE_COUNT - 1 : null;

  function gradientEndpoints(boundaryAngle: number, directionSign: 1 | -1) {
    const r = 1;
    const startAngle = boundaryAngle;
    const endAngle = boundaryAngle + directionSign * sliceAngle;

    const x1 = Math.cos(startAngle) * r;
    const y1 = Math.sin(startAngle) * r;
    const x2 = Math.cos(endAngle) * r;
    const y2 = Math.sin(endAngle) * r;

    return { x1, y1, x2, y2 };
  }

  const frontBoundaryAngle =
    frontIndex !== null ? base + frontIndex * sliceAngle : null;

  const backBoundaryAngle = backIndex !== null ? base : null;

  const frontGrad =
    frontBoundaryAngle !== null
      ? gradientEndpoints(frontBoundaryAngle, +1)
      : null;

  const backGrad =
    backBoundaryAngle !== null
      ? gradientEndpoints(backBoundaryAngle, -1)
      : null;

  return (
    <svg
      viewBox="-1 -1 2 2"
      className={cn("absolute inset-0 pointer-events-none", className)}
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        {frontGrad && (
          <linearGradient
            id="slice-grad-front"
            gradientUnits="userSpaceOnUse"
            x1={frontGrad.x1}
            y1={frontGrad.y1}
            x2={frontGrad.x2}
            y2={frontGrad.y2}
          >
            <stop offset="0%" stopColor="#D9D9D9" stopOpacity="0" />
            <stop offset="40%" stopColor="#D9D9D9" stopOpacity="1" />
            <stop offset="100%" stopColor="#D9D9D9" stopOpacity="1" />
          </linearGradient>
        )}

        {backGrad && (
          <linearGradient
            id="slice-grad-back"
            gradientUnits="userSpaceOnUse"
            x1={backGrad.x1}
            y1={backGrad.y1}
            x2={backGrad.x2}
            y2={backGrad.y2}
          >
            <stop offset="0%" stopColor="#D9D9D9" stopOpacity="0" />
            <stop offset="40%" stopColor="#D9D9D9" stopOpacity="1" />
            <stop offset="100%" stopColor="#D9D9D9" stopOpacity="1" />
          </linearGradient>
        )}
      </defs>

      {Array.from({ length: SLICE_COUNT }).map((_, i) => {
        const start = base + i * sliceAngle;
        const end = start + sliceAngle;

        let fill: string = "#D9D9D9";

        if (hasBorders) {
          if (i === frontIndex) fill = "url(#slice-grad-front)";
          if (i === backIndex) fill = "url(#slice-grad-back)";
        }

        return (
          <path
            key={i}
            d={sectorPath(start, end)}
            fill={fill}
            className="mix-blend-color"
            style={{ transition: "opacity 200ms ease" }}
            opacity={i < removedCount ? 0 : 1}
          />
        );
      })}
    </svg>
  );
}

export default function WhySection({
  data,
  className,
  futures = false,
}: {
  data: PageHomeSchema | PageFuturesSchema;
  className?: string;
  futures?: boolean;
}) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      setCurrent(api.selectedScrollSnap());
      setCanPrev(api.canScrollPrev());
      setCanNext(api.canScrollNext());
    };

    onSelect();
    api.on("select", onSelect);
    api.on("reInit", onSelect);
    return () => {
      api.off("select", onSelect);
      api.off("reInit", onSelect);
    };
  }, [api]);

  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const sectionRef = useRef<HTMLElement | null>(null);
  const itemsContainerRef = useRef<HTMLDivElement | null>(null);
  const leftSideContainerRef = useRef<HTMLDivElement | null>(null);
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  const [revealedCount, setRevealedCount] = useState(0);

  useEffect(() => {
    if (!isLargeScreen && api) {
      setRevealedCount(current + 1);
    }
  }, [current, isLargeScreen, api]);

  const itemCount = data?.whyItems?.length || 0;

  // Mobile scroll-driven carousel
  useEffect(() => {
    if (!sectionRef.current || isLargeScreen || !api || itemCount === 0) return;

    const section = sectionRef.current;
    const sectionHeight = section.offsetHeight;
    const viewportHeight = window.innerHeight;
    const totalScrollRange = sectionHeight - viewportHeight;

    const animationRange = totalScrollRange * 0.75;
    const startOffset = totalScrollRange * 0.1;

    ScrollTrigger.create({
      trigger: section,
      start: "top top",
      end: "bottom bottom",
      scrub: 0.5,
      onUpdate: (self) => {
        const progress = self.progress;

        // Calculate which slide to show based on scroll progress
        const scrollPosition = startOffset + progress * totalScrollRange;
        const normalizedProgress =
          (scrollPosition - startOffset) / (animationRange - startOffset);
        const clampedProgress = Math.max(0, Math.min(1, normalizedProgress));

        const targetSlide = Math.floor(clampedProgress * itemCount);
        const slideIndex = Math.min(targetSlide, itemCount - 1);

        if (api.selectedScrollSnap() !== slideIndex) {
          api.scrollTo(slideIndex, false);
        }
      },
    });

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => {
        if (trigger.vars.trigger === section) {
          trigger.kill();
        }
      });
    };
  }, [isLargeScreen, api, itemCount]);

  useEffect(() => {
    const handleResize = () => {
      const isLg = window.innerWidth >= 1024;
      setIsLargeScreen(isLg);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (
      !sectionRef.current ||
      !isLargeScreen ||
      !itemRefs.current.length ||
      itemCount === 0
    )
      return;

    const section = sectionRef.current;
    const items = itemRefs.current.filter((item) => item !== null);
    if (!items.length) return;

    // Delay to allow previous sections to calculate their heights
    const initDelay = setTimeout(() => {
      ScrollTrigger.refresh();
    }, 1000);

    gsap.set(items, { opacity: 0.3 });

    const sectionHeight = section.offsetHeight;
    const viewportHeight = window.innerHeight;
    const totalScrollRange = sectionHeight - viewportHeight;

    const animationRange = totalScrollRange * 0.75;
    const startOffset = totalScrollRange * 0.1;

    const scrollItemsContainer = (targetIndex: number) => {
      if (!leftSideContainerRef.current || !items[targetIndex]) return;

      const container = leftSideContainerRef.current;
      const targetItem = items[targetIndex];
      const containerRect = container.getBoundingClientRect();
      // @ts-ignore
      const itemRect = targetItem.getBoundingClientRect();

      const itemTopRelativeToContainer = itemRect.top - containerRect.top;
      const containerCenter = containerRect.height / 2;
      const itemCenter = itemTopRelativeToContainer + itemRect.height / 2;

      if (itemCenter > containerCenter) {
        const scrollAmount = (itemCenter - containerCenter) * 0.5;
        gsap.to(container, {
          y: -scrollAmount,
          duration: 0.5,
          ease: "power2.out",
        });
      }
    };

    items.forEach((item, index) => {
      const itemScrollPosition =
        startOffset +
        (index / (itemCount - 1)) * (animationRange - startOffset);

      ScrollTrigger.create({
        trigger: section,
        start: `top+=${itemScrollPosition}px top`,
        onEnter: () => {
          gsap.killTweensOf(items);

          items.forEach((otherItem, otherIndex) => {
            if (otherIndex !== index) {
              gsap.to(otherItem, {
                opacity: 0.3,
                duration: 0.3,
                ease: "power2.out",
              });
            }
          });
          gsap.to(item, { opacity: 1, duration: 0.4, ease: "power2.out" });
          scrollItemsContainer(index);
          setRevealedCount(index + 1);
        },
        onLeaveBack: () => {
          gsap.killTweensOf(items);

          items.forEach((otherItem) => {
            gsap.to(otherItem, {
              opacity: 0.3,
              duration: 0.3,
              ease: "power2.in",
            });
          });

          if (index > 0) {
            gsap.to(items[index - 1], {
              opacity: 1,
              duration: 0.4,
              ease: "power2.out",
            });
            scrollItemsContainer(index - 1);
          } else {
            if (leftSideContainerRef.current) {
              gsap.to(leftSideContainerRef.current, {
                y: 0,
                duration: 0.5,
                ease: "power2.out",
              });
            }
          }
          setRevealedCount(index);
        },
      });
    });

    return () => {
      clearTimeout(initDelay);
      ScrollTrigger.getAll().forEach((trigger) => {
        if (trigger.vars.trigger === section) {
          trigger.kill();
        }
      });
    };
  }, [isLargeScreen, itemCount]);

  return (
    <section
      ref={sectionRef}
      className={cn(
        "relative overflow-clip lg:h-[450vh] pt-[8rem] lg:pt-[13rem] h-[320vh]",
        className,
      )}
    >
      <div className={cn("sticky w-[min(229.25rem,100%)] mx-auto rounded-b-[2rem] lg:rounded-b-[5rem]", data?.flashSaleText ? "top-[22rem] lg:top-[10rem]" : "top-[10rem]", futures ? "bg-[#080713]" : "bg-[#07171B]")}>
        <div className="relative w-full h-full">
          <div className={cn("absolute left-0 bottom-full w-full translate-y-px h-[8rem] rounded-t-[2rem] lg:h-[13rem] lg:rounded-t-[5rem]", futures ? "bg-[#080713]" : "bg-[#07171B]")} />

          <div className="container flex flex-col items-center">
            {data?.whyTitle && (
              <div className="lg:hidden richtext lg:richtext-start prose-headings:text-white prose-p:text-white/70 w-[min(60rem,100%)] mb-[3rem] lg:mb-[7rem]">
                <PortableText
                  value={data.whyTitle}
                  components={richTextComponent}
                />
              </div>
            )}

            <div className="w-[min(193rem,100%)] flex flex-col lg:flex-row-reverse lg:justify-between items-start">
              <div className="w-[min(138.125rem,100%)] relative">
                <Image className="!w-full" src={clockTower} alt="" />

                <div className="absolute rounded-full w-[56.25%] left-[22.25%] bottom-[8.3%] aspect-square overflow-clip">
                  <PieOverlay
                    className="absolute inset-0"
                    removedCount={revealedCount}
                  />
                </div>

                <div className={cn("absolute bottom-0 left-0 w-full bg-gradient-to-t h-[20rem]", futures ? "from-[#080713] to-[#080713]/0" : "from-[#07171B] to-[#07171B]/0")} />
              </div>

              {data?.whyItems && (
                <div className="w-full lg:hidden mt-[4rem]">
                  <Carousel
                    className="w-full"
                    setApi={setApi}
                    opts={{
                      align: "start",
                      skipSnaps: false,
                      watchDrag: false,
                    }}
                  >
                    <CarouselContent className="w-full gap-x-[2rem]">
                      {data?.whyItems?.map((item, index) => (
                        <CarouselItem key={index} className="p-0 basis-full">
                          <div className={cn("flex flex-shrink-0 items-center border gap-[2.5rem] border-[#615B50]/10 rounded-[2rem] p-[2rem]", futures ? "bg-[#0D0E27]" : "bg-[#0C1F24]")}>
                            {item?.icon && (
                              <Image
                                className="!w-[6.375rem] rounded-[1.375rem] flex-shrink-0"
                                src={item.icon}
                                alt=""
                              />
                            )}

                            {item?.text && (
                              <div className="richtext richtext-start prose-p:text-white/70 prose-p:leading-[1.4] prose-p:text-[1.875rem] lg:prose-p:text-[2rem]">
                                <PortableText
                                  value={item.text}
                                  components={richTextComponent}
                                />
                              </div>
                            )}
                          </div>
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                  </Carousel>

                  <div className="w-[min(55rem,100%)] mx-auto h-[2px] bg-white/20 flex mt-[2.5rem]">
                    {data?.whyItems?.map((_, index) => (
                      <div
                        key={index}
                        className={cn(
                          "grow h-full transition-colors",
                          index > current ? "transparent" : "bg-[#E9D7A6]",
                        )}
                      />
                    ))}
                  </div>
                </div>
              )}

              <div className="w-[min(46.875rem,100%)] hidden lg:flex flex-col items-start relative">
                <div
                  className="absolute top-0 left-0 w-full"
                  ref={leftSideContainerRef}
                >
                  {data?.whyTitle && (
                    <div className="block richtext lg:richtext-start prose-headings:text-white prose-p:text-white/70 w-[min(60rem,100%)] mb-[1rem] lg:mb-[5rem]">
                      <PortableText
                        value={data.whyTitle}
                        components={richTextComponent}
                      />
                    </div>
                  )}

                  {data?.whyItems && (
                    <div
                      ref={itemsContainerRef}
                      className="flex lg:flex-col gap-[1.5rem]"
                    >
                      {data.whyItems.map((item, index) => (
                        <div
                          key={index}
                          ref={(el) => (itemRefs.current[index] = el)}
                          className={cn("flex items-center gap-[2.5rem] border border-[#615B50]/10 rounded-[2rem] p-[2rem] w-[min(44.125rem,100%)] flex-shrink-0", futures ? "bg-[#0D0E27]" : "bg-[#0C1F24]")}
                        >
                          {item?.icon && (
                            <Image
                              className="!w-[6.375rem] rounded-[1.375rem] flex-shrink-0"
                              src={item.icon}
                              alt=""
                            />
                          )}

                          {item?.text && (
                            <div className="richtext richtext-start prose-p:text-white/70 prose-p:leading-[1.4]">
                              <PortableText
                                value={item.text}
                                components={richTextComponent}
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
